import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import CharacterList from './components/shared/CharacterList';
import CharacterSheet from './components/CharacterSheet';
import Worlds from './components/shared/Worlds';
import WorldDetail from './components/shared/WorldDetail';
import Login from './components/shared/Login';
import Setup from './components/shared/Setup';
import ShareView from './components/shared/ShareView';
import { applyTheme } from './theme';

function RequireAuth({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function SheetRoute({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <CharacterSheet
      charId={id}
      onBack={() => navigate('/characters')}
      user={user}
    />
  );
}

function WorldDetailRoute({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  return <WorldDetail user={user} worldId={id} onBack={() => navigate('/worlds')} />;
}

// An external, read-only world share link. Same component as the logged-in
// world view — it just draws its data from the share prefix instead (see
// worldApi in api.js). There is no `user`, and the server pins the viewer to the
// 'read' tier, so every editing and management affordance is already absent.
function WorldShareRoute() {
  const { token } = useParams();
  return <WorldDetail worldId={null} shareToken={token} />;
}

function AppRoutes({ user, setUser, needsSetup, setNeedsSetup }) {
  const navigate = useNavigate();

  const logout = async () => {
    await fetch('/api/logout', { method: 'POST' });
    setUser(null);
    navigate('/login');
  };

  const openSheet = (id) => navigate(`/characters/${id}`);

  return (
    <Routes>
      <Route path="/setup" element={
        needsSetup && !user
          ? <Setup onSetup={(u) => {
              setNeedsSetup(false);
              setUser(u);
              applyTheme(u?.theme || 'tavern');
              navigate('/characters');
            }} />
          : <Navigate to={user ? '/characters' : '/login'} replace />
      } />
      <Route path="/login" element={
        needsSetup ? <Navigate to="/setup" replace />
          : user ? <Navigate to="/characters" replace />
          : <Login onLogin={(u) => { setUser(u); applyTheme(u?.theme || 'tavern'); navigate('/characters'); }} />
      } />
      <Route path="/characters" element={
        <RequireAuth user={user}>
          <CharacterList
            onSelect={openSheet}
            onNew={openSheet}
            user={user}
            onUser={setUser}
            onLogout={logout}
            onWorlds={() => navigate('/worlds')}
          />
        </RequireAuth>
      } />
      <Route path="/characters/:id" element={
        <RequireAuth user={user}>
          <SheetRoute user={user} />
        </RequireAuth>
      } />
      {/* §4.8: worlds are no longer GM-only — a non-GM player who's a ledger
          MEMBER of a world reaches it read-only here. The server is the gate
          (GET /api/worlds returns only worlds you own or belong to; the detail
          view renders off the role it returns), so route auth is just RequireAuth. */}
      <Route path="/worlds" element={
        <RequireAuth user={user}>
          <Worlds user={user} onBack={() => navigate('/characters')} onOpenWorld={(id) => navigate(`/worlds/${id}`)} />
        </RequireAuth>
      } />
      <Route path="/worlds/:id" element={
        <RequireAuth user={user}>
          <WorldDetailRoute user={user} />
        </RequireAuth>
      } />
      <Route path="/share/:token" element={<ShareView />} />
      <Route path="/world-share/:token" element={<WorldShareRoute />} />
      <Route path="*" element={
        <Navigate to={needsSetup ? '/setup' : (user ? '/characters' : '/login')} replace />
      } />
    </Routes>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const meRes = await fetch('/api/me');
        const data = meRes.ok ? await meRes.json() : null;
        if (data) {
          setUser(data);
          applyTheme(data.theme || 'tavern');           // personal theme
        } else {
          // Not logged in. Is this a fresh, user-less install?
          try {
            const setupRes = await fetch('/api/setup');
            const s = setupRes.ok ? await setupRes.json() : null;
            if (s?.needsSetup) setNeedsSetup(true);
          } catch {}
          // Logged out / share page — use the admin default theme.
          // Fire-and-forget: don't block setChecking on the theme fetch.
          fetch('/api/settings')
            .then(r => r.ok ? r.json() : null)
            .then(s => applyTheme(s?.theme || 'tavern'))
            .catch(() => {});
        }
      } catch {}
      setChecking(false);
    };
    run();
  }, []);

  if (checking) return null;

  return (
    <BrowserRouter>
      <AppRoutes user={user} setUser={setUser} needsSetup={needsSetup} setNeedsSetup={setNeedsSetup} />
    </BrowserRouter>
  );
}
